import { useState, useContext, useEffect, useRef } from "react";
import { FaCircle } from "react-icons/fa";
import ScreenshotButtonComponent2 from "./components/ScreenshotButtonComponent2";
import StudentEmotionsDistribution from "./StudentDashboard/StudentEmotionsDistribution";
import { MyStoryContext } from "../../context/myStory.context";
import DateframeDropdownSelect from "../../components/DateframeDropdownSelect";
import WhatHelps from "./StudentDashboard/WhatHelps";
import { StudentTrends } from "./StudentDashboard/StudentTrends";
import AchievementsPopup from "../../components/AchievementPage/AchievementPopup";
import { useLocation } from "react-router-dom";
import { HeartPlus } from "lucide-react";
import { useOnboardingUiEnabled } from "@/hooks/useOnboardingUiEnabled";
import { useIsMobileViewport } from "@/hooks/useIsMobileViewport";
import { useBootstrap } from "@/features/bootstrap";

export default function StudentDashboard() {
  const { data: user } = useBootstrap();
  const {
    bigFiveFilteredData,
    selectedTimes,
    setSelectedTimes,
    helpfulFilter,
    setHelpfulFilter,
    selectedSubOptions,
    setSelectedSubOptions,
    state,
    setState,
    filterName,
    wellnessPopupOpen,
    setWellnessPopupOpen,
    selectedEmotion,
    setSelectedEmotion,
    selectedEmotionColor,
    setSelectedEmotionColor,
    selectedTimeframe,
    feeling,
    barGraphData,
    tally,
    compareTally,
    gatherWellnessPractices,
    studentDashboardWhatHelps,
  } = useContext(MyStoryContext);

  const location = useLocation();
  const isRefreshingData = location.state?.refreshingData;

  const [showPopup, setShowPopup] = useState(true);
  const [isDataLoading, setIsDataLoading] = useState(false); // Start as false
  const goalDayRef = useRef(null);
  const goalWeekRef = useRef(null);

  // Track initial count when refresh starts
  const initialCountRef = useRef(tally);

  // Only start loading if we're actually refreshing data
  useEffect(() => {
    if (isRefreshingData) {
      // Store initial tally count (the count displayed in the popup)
      initialCountRef.current = tally;

      setIsDataLoading(true);

      // Fallback timer - stop loading after 30 seconds max
      const timer = setTimeout(() => {
        setIsDataLoading(false);
      }, 30000);

      return () => clearTimeout(timer);
    }
  }, [isRefreshingData]); // Only depend on isRefreshingData

  // Monitor DOM changes for the count display in the popup using polling
  useEffect(() => {
    if (isDataLoading && isRefreshingData) {
      // Use polling to check the DOM value every 100ms
      const pollInterval = setInterval(() => {
        const countElement = document.querySelector("#wellness-count-display");
        if (countElement) {
          const displayedCount = parseInt(
            countElement.textContent.replace(/,/g, "").trim(),
          );

          if (
            !isNaN(displayedCount) &&
            displayedCount > initialCountRef.current
          ) {
            setIsDataLoading(false);
            clearInterval(pollInterval);
          }
        }
      }, 100);

      // Fallback timeout to force stop loading if nothing changes
      const fallbackTimeout = setTimeout(() => {
        setIsDataLoading(false);
        clearInterval(pollInterval);
      }, 30000);

      // Cleanup
      return () => {
        clearInterval(pollInterval);
        clearTimeout(fallbackTimeout);
      };
    }
  }, [isDataLoading, isRefreshingData]);

  // Clear the navigation state after we've processed it
  useEffect(() => {
    if (isRefreshingData && !isDataLoading) {
      // Clear the navigation state so it doesn't trigger again
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [isRefreshingData, isDataLoading]);

  useEffect(() => {
    if (showPopup) {
      goalDayRef.current?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
      goalWeekRef.current?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [showPopup]);

  const emotionData = barGraphData || [];

  const emotionPercents = emotionData.map((d) => {
    const currentPercent = tally ? Math.round((d.count / tally) * 100) : 0;
    const comparePercent = compareTally
      ? Math.round((d.compare / compareTally) * 100)
      : 0;
    const change = currentPercent - comparePercent;
    return {
      ...d,
      currentPercent,
      comparePercent,
      change,
    };
  });

  // Calculate the appropriate count to display
  const getDisplayCount = () => {
    if (isRefreshingData && isDataLoading) {
      // If we're refreshing data after wellness practice, increment the current tally
      return tally + 1;
    }
    return tally;
  };

  const noMyStoryData = !tally || tally <= 0;
  const isTourActive = useOnboardingUiEnabled();
  const isMobileViewport = useIsMobileViewport();
  const demoImgSrc = (src) =>
    isMobileViewport ? src.replace(/-demo(\.[a-z]+)$/i, "-mobile$1") : src;
  const showTourDemoWidgets = isTourActive && noMyStoryData && !isRefreshingData;

  return (
    // wrap main component with tw container to apply Tailwind styles
    <div className="tw">
      <div className="sm:ml-[130px] sm:min-h-[100vh] sm:mt-0 mt-16 border border-[#DDDDDD] bg-[#F4F4F3] flex flex-col"
      >
        {/* Header Section */}
        <div
          className="flex justify-between items-center w-full gap-2 shadow-md px-3 py-3 sm:px-8 sm:py-4 bg-[#FFFFFF] sticky top-0 z-10"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center sm:space-x-2">
            <span className="text-[24px] ml-1 text-500 font-medium">
              My Wellness Data
            </span>
            <FaCircle className="sm:w-2.5 sm:h-2.5 w-0 h-0 text-600 text-[#595959]" />
            <DateframeDropdownSelect
              value={filterName}
              onChange={(value) => gatherWellnessPractices(feeling, value)}
              className="bg-white opacity-75 text-[24px]"
            />
          </div>
          <div className="sm:mr-3">
            <ScreenshotButtonComponent2 />
          </div>
        </div>
        <div className="px-3 sm:px-8 pb-10 sm:pt-4 pt-3 flex-1"
            data-onboarding-id="mystory-top"
        >
          <AchievementsPopup
            show={showPopup}
            coins={user.coins}
            displayName={user.displayName}
            email={user.email}
            chosenTime={state.chosenTime}
            count={tally}
            time={filterName}
            badges={user.badges}
            onClose={() => setShowPopup(false)}
            curDayStreak={user.curDayStreak}
            curWeekStreak={user.curWeekStreak}
            maxDayStreak={user.maxDayStreak}
            maxWeekStreak={user.maxWeekStreak}
            goalDayRef={goalDayRef}
            goalWeekRef={goalWeekRef}
            isLoading={isDataLoading}
          ></AchievementsPopup>

          {noMyStoryData && !isRefreshingData ? (
            <div
              className="flex flex-col items-center justify-center gap-3 text-400 bg-white shadow-sm border-2 border-dotted rounded p-8 mt-3"
              data-onboarding-no-data="my-story"
            >
              <div className="flex items-center justify-center gap-2">
                <HeartPlus className="w-10 h-10 text-[#2D374840]" />
                <span className="text-[16px]">
                  No wellness practices yet. Start practicing to see your
                  wellness data.
                </span>
              </div>
            </div>
          ) : null}

          {/* <NoData hasStudents={state.currentStudents.length > 0} hasActivity={state.actionsCounter > 0} /> */}
          {showTourDemoWidgets ? (
            <div className="flex-1 bg-white shadow-md rounded p-3 sm:p-4 2xl:p-8 mt-3">
              <div className="inline-flex items-center rounded-md bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-900 ring-1 ring-amber-200">
                Example with demo data
              </div>
              <img
                src={demoImgSrc("/images/onboardingDemo/trends-demo.png")}
                alt="My Story trends example (demo only)"
                className="mt-2 w-full object-contain rounded-xl border border-black/10 bg-white"
                loading="lazy"
                draggable={false}
              />
            </div>
          ) : (
            <StudentTrends
              state={state}
              emotionPercents={emotionPercents}
              tally={tally}
              compareTally={compareTally}
              // Use time-filtered data for current timeframe participation
              filterName={filterName}
            />
          )}
          <div className="flex xl:flex-row flex-col gap-3 mt-3">
            <div className="flex-1 flex flex-col gap-3">
              {showTourDemoWidgets ? (
                <div className="flex-1 bg-white shadow-md rounded p-3 sm:p-4 2xl:p-8">
                  <div className="inline-flex items-center rounded-md bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-900 ring-1 ring-amber-200">
                    Example with demo data
                  </div>
                  <img
                    src={demoImgSrc("/images/onboardingDemo/pie-demo.png")}
                    alt="My Story emotions breakdown example (demo only)"
                    className="mt-2 w-full object-contain rounded-xl border border-black/10 bg-white"
                    loading="lazy"
                    draggable={false}
                  />
                </div>
              ) : (
                <StudentEmotionsDistribution
                  state={state}
                  emotionPercents={emotionPercents}
                  tally={tally}
                  updateState={setState}
                  userRole={user.role}
                  gatherWellnessPractices={gatherWellnessPractices}
                  filterName={filterName}
                  feeling={feeling}
                  bigFiveFilteredData={bigFiveFilteredData}
                  selectedTimes={selectedTimes}
                  setSelectedTimes={setSelectedTimes}
                  helpfulFilter={helpfulFilter}
                  setHelpfulFilter={setHelpfulFilter}
                  selectedSubOptions={selectedSubOptions}
                  setSelectedSubOptions={setSelectedSubOptions}
                  wellnessPopupOpen={wellnessPopupOpen}
                  setWellnessPopupOpen={setWellnessPopupOpen}
                  adultActionImages={user.adultActionImages}
                />
              )}
            </div>
            <div className="flex-1 flex flex-col gap-3">
              <div
                className="transition-all duration-300 ease-in-out relative"
                data-onboarding-id="emotions-helping"
              >
                {showTourDemoWidgets ? (
                  <div className="flex-1 bg-white shadow-md rounded p-3 sm:p-4 2xl:p-8">
                    <div className="inline-flex items-center rounded-md bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-900 ring-1 ring-amber-200">
                      Example with demo data
                    </div>
                    <img
                      src={demoImgSrc(
                        "/images/onboardingDemo/mystory-whathelps-demo.png",
                      )}
                      alt="My Story what's helping example (demo only)"
                      className="mt-2 w-full object-contain rounded-xl border border-black/10 bg-white"
                      loading="lazy"
                      draggable={false}
                    />
                  </div>
                ) : (
                  <WhatHelps
                      state={state}
                    studentData={studentDashboardWhatHelps}
                    selectedTimeframe={selectedTimeframe}
                    selectedEmotion={selectedEmotion}
                    setSelectedEmotion={setSelectedEmotion}
                    selectedEmotionColor={selectedEmotionColor}
                    setSelectedEmotionColor={setSelectedEmotionColor}
                    emotionPercents={emotionPercents}
                    adultActionImages={user.adultActionImages}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
